import { useEffect, useRef, useState } from 'react';

import type { MeResponse } from '@time-tracking/shared';

import { useFocusTrap } from '../../../hooks/useFocusTrap.js';

type TeamOption = MeResponse['teams'][number];

interface NewProjectDialogProps {
  open: boolean;
  teams: TeamOption[];
  onClose: () => void;
  onSubmit: (data: { name: string; code: string; description: string; teamId: string }) => void;
  isPending: boolean;
}

export function NewProjectDialog({
  open,
  teams,
  onClose,
  onSubmit,
  isPending,
}: NewProjectDialogProps) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [teamId, setTeamId] = useState(teams[0]?.teamId ?? '');
  const [touched, setTouched] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  useFocusTrap(dialogRef, open);

  useEffect(() => {
    if (!open) {
      setName('');
      setCode('');
      setDescription('');
      setTouched(false);
      setTeamId(teams[0]?.teamId ?? '');
      return;
    }
    setTimeout(() => firstInputRef.current?.focus(), 0);
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose, teams]);

  if (!open) return null;

  const nameError = touched && name.trim().length === 0 ? 'Name is required' : null;
  const codeError = touched && code.trim().length === 0 ? 'Code is required' : null;
  const teamError = touched && !teamId ? 'Team is required' : null;
  const canSubmit = name.trim() && code.trim() && teamId && !isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    onSubmit({ name: name.trim(), code: code.trim(), description: description.trim(), teamId });
  }

  return (
    <div
      className="dialog-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-project-dialog-title"
        className="projects-dialog projects-dialog--wide"
      >
        <h2 id="new-project-dialog-title" className="dialog-title">
          New project
        </h2>
        <p className="dialog-subtitle">
          You&apos;ll be set as the project lead. You can add tasks and assign people right after
          creating.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="dialog-form-field">
            <label htmlFor="proj-name">
              Project name
              <span className="required-indicator" aria-hidden="true">
                {' '}
                *
              </span>
              <span className="sr-only"> (required)</span>
            </label>
            <input
              ref={firstInputRef}
              id="proj-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. IGA Phase 3 — Prod cutover"
              aria-required="true"
              aria-invalid={nameError ? 'true' : undefined}
              aria-describedby={nameError ? 'proj-name-err' : undefined}
              maxLength={255}
            />
            {nameError && (
              <span id="proj-name-err" className="dialog-form-field__error" role="alert">
                {nameError}
              </span>
            )}
          </div>

          <div className="dialog-row2">
            <div className="dialog-form-field">
              <label htmlFor="proj-code">
                Code
                <span className="required-indicator" aria-hidden="true">
                  {' '}
                  *
                </span>
                <span className="sr-only"> (required)</span>
              </label>
              <input
                id="proj-code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s/g, '-'))}
                placeholder="IAM-IGA-P3"
                aria-required="true"
                aria-invalid={codeError ? 'true' : undefined}
                aria-describedby={codeError ? 'proj-code-err' : undefined}
                maxLength={64}
                style={{ fontFamily: 'var(--font-mono)' }}
              />
              {codeError && (
                <span id="proj-code-err" className="dialog-form-field__error" role="alert">
                  {codeError}
                </span>
              )}
            </div>

            <div className="dialog-form-field">
              <label htmlFor="proj-team">
                Team
                <span className="required-indicator" aria-hidden="true">
                  {' '}
                  *
                </span>
                <span className="sr-only"> (required)</span>
              </label>
              {teams.length === 0 ? (
                <p
                  className="dialog-hint"
                  style={{ color: 'var(--color-destructive)', marginTop: 0 }}
                >
                  You are not a member of any team. Ask an administrator to add you first.
                </p>
              ) : (
                <select
                  id="proj-team"
                  value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                  aria-required="true"
                  aria-invalid={teamError ? 'true' : undefined}
                  aria-describedby={teamError ? 'proj-team-err' : undefined}
                  disabled={teams.length === 1}
                >
                  {teams.length > 1 && <option value="">Select a team…</option>}
                  {teams.map((t) => (
                    <option key={t.teamId} value={t.teamId}>
                      {t.name}
                    </option>
                  ))}
                </select>
              )}
              {teamError && (
                <span id="proj-team-err" className="dialog-form-field__error" role="alert">
                  {teamError}
                </span>
              )}
            </div>
          </div>

          <div className="dialog-form-field">
            <label htmlFor="proj-desc">Short description</label>
            <textarea
              id="proj-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="One sentence — what's this project for?"
              rows={2}
              maxLength={2000}
            />
          </div>

          <div className="dialog-footer">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={!canSubmit}
              aria-busy={isPending}
            >
              {isPending ? 'Creating…' : 'Create project'}
            </button>
            <button type="button" className="btn btn--secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
